import { create } from 'zustand'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'

// The opportunity list endpoint serves 20 cards per page (CrmPagination). Kept
// here so a caller can size an initial window without re-deriving it.
export const PAGE_SIZE = 20

// Board state for the CRM pipeline: the account's stages (columns) and the
// OPEN opportunities (cards). Card moves are optimistic — we update locally,
// PATCH the backend, and roll back if it fails.
//
// Each column paginates on its own. The board used to walk every page of the
// opportunity list until has_more went false — 30+ sequential requests before
// it could paint, growing with the account's whole history — and then discarded
// the WON/LOST half in JavaScript. Now every request carries
// `status=OPEN&stage=<id>`, only page 1 of each column loads up front, and a
// column asks for its next page when the user scrolls that column to the end.
// The counts the board shows come from the aggregate endpoint rather than from
// the cards in memory, because the cards are no longer the whole set.

const emptyColumn = () => ({
    // Cards loaded so far for this column, oldest page first.
    rows: [],
    // Highest page loaded. The next scroll asks for `page + 1`.
    page: 0,
    // Straight from the response — never inferred from rows.length.
    hasMore: true,
    // Page 1 in flight — a first load, or a refresh over cards still shown…
    loading: false,
    // …versus a later page in flight (appending under the cards).
    // Separate flags so a column can render "loading more" under its cards
    // instead of blanking them, and so the lazy trigger has one thing to check.
    loadingMore: false,
    error: false,
})

const emptyCounts = () => ({ total: 0, stages: {}, owners: [] })

// Stage ids key `byStage` and the counts response as strings — JSON object keys
// always are, and a numeric id would otherwise index the two differently.
const keyOf = (stageId) => String(stageId)

export const usePipelineStore = create((set, get) => ({
    stages: [],
    // { [stageId]: column }. One independently paginated column per stage.
    byStage: {},
    // Server-side aggregates: { total, stages: {id: n}, owners: [{owner_id, name, count}] }.
    // `total` and `owners` describe the whole open pipeline whatever is
    // filtered — they are what the user filters *with* — while `stages` follows
    // the active search and rep filter, because the column headers count the
    // cards in the columns. That split is the backend's; see views.counts.
    counts: emptyCounts(),
    // The board's own first load: stages + counts + page 1 of every column.
    loading: false,

    // Board filter UI state. It lives here rather than in PipelinePage's local
    // state because opening a card routes to /crm/people/:id, which unmounts the
    // board — the user's search and rep filter have to survive coming back.
    // On the CRM route the rep filter is the topbar view's owner (see
    // fetchPipeline); only the attach picker's chips set it themselves.
    // They are query parameters now rather than a client-side filter, so a match
    // sitting on a page nobody has scrolled to is still found; every request
    // reads whatever they hold, which is what carries them across the remount.
    search: '',
    repFilter: 'all',
    setSearch: (search) => set({ search }),
    // Chips are clicks, not typing, so this applies immediately — no debounce.
    setRepFilter: (repFilter) => {
        if (get().repFilter === repFilter) return
        set({ repFilter })
        get().applyFilters()
    },

    // Scroll offsets of the .board-wrap container, saved on scroll and restored
    // on remount for the same reason. Written imperatively (setState, never
    // subscribed) so scrolling doesn't re-render the board.
    scroll: { left: 0, top: 0 },

    // Bumped per column on every request so a response for a superseded page,
    // search term or rep filter is dropped instead of being appended into the
    // newer result set. The counts request carries its own. Same guard
    // crmPeopleStore uses, for the same reason.
    _tokens: {},
    _countsToken: 0,

    // The query every column request shares: OPEN only (the board never shows
    // anything else — it used to fetch WON/LOST and discard them client-side),
    // plus whichever filters are active.
    _filterParams: () => {
        const params = new URLSearchParams({ status: 'OPEN' })
        const term = get().search.trim()
        if (term) params.set('search', term)
        const rep = get().repFilter
        if (rep !== 'all') params.set('owner', String(rep))
        return params
    },

    // Load one page of one column. Page 1 replaces the column's cards; a later
    // page appends. Nothing here follows `has_more` on its own — that is the
    // whole point — it loads exactly the page it was asked for.
    fetchStagePage: async (stageId, page = 1) => {
        const key = keyOf(stageId)
        const token = (get()._tokens[key] || 0) + 1
        set((state) => ({
            _tokens: { ...state._tokens, [key]: token },
            byStage: {
                ...state.byStage,
                [key]: {
                    ...(state.byStage[key] || emptyColumn()),
                    loading: page === 1,
                    loadingMore: page > 1,
                    error: false,
                },
            },
        }))

        const params = get()._filterParams()
        params.set('stage', key)
        params.set('page', String(page))

        try {
            const res = await fetcher.getCrmOpportunities(params)
            if (get()._tokens[key] !== token) return
            const { results = [], has_more = false } = res?.data || {}
            set((state) => {
                const column = state.byStage[key] || emptyColumn()
                // Append deduped by id. A card can legitimately arrive twice — a
                // drag/drop dropped one into this column before its real page
                // loaded, or a create shifted every later row down by one — and
                // a duplicate key breaks rendering as well as the count.
                let rows
                if (page === 1) {
                    rows = results
                } else {
                    const seen = new Set(column.rows.map((o) => o.id))
                    rows = column.rows.concat(results.filter((o) => !seen.has(o.id)))
                }
                return {
                    byStage: {
                        ...state.byStage,
                        [key]: {
                            ...column,
                            rows,
                            page,
                            hasMore: has_more,
                            loading: false,
                            loadingMore: false,
                            error: false,
                        },
                    },
                }
            })
        } catch (e) {
            if (get()._tokens[key] !== token) return
            // A failed page 2 must not erase page 1: only the flags are cleared,
            // so the cards stay put and the same trigger can retry.
            set((state) => ({
                byStage: {
                    ...state.byStage,
                    [key]: {
                        ...(state.byStage[key] || emptyColumn()),
                        loading: false,
                        loadingMore: false,
                        error: true,
                    },
                },
            }))
            toast.error('Could not load that pipeline column.')
        }
    },

    // What a column's lazy trigger calls. Every guard that stops the board from
    // walking pages on its own lives here: nothing loaded yet, a request already
    // in flight, or the server said that was the last page.
    // The card a deep link (?opportunity=<id>) pointed at, so the board can
    // highlight it. Transient presentation state, cleared on a timer by the
    // hook that sets it — it is not part of the board's data.
    focusedOpportunityId: null,
    setFocusedOpportunity: (id) => set({ focusedOpportunityId: id }),

    loadMoreForStage: async (stageId) => {
        const column = get().byStage[keyOf(stageId)]
        if (!column) return
        if (column.loading || column.loadingMore || !column.hasMore) return
        await get().fetchStagePage(stageId, column.page + 1)
    },

    refreshCounts: async () => {
        const token = get()._countsToken + 1
        set({ _countsToken: token })
        try {
            const res = await fetcher.getCrmOpportunityCounts(get()._filterParams())
            if (get()._countsToken !== token) return
            const { total = 0, stages = {}, owners = [] } = res?.data || {}
            set({ counts: { total, stages, owners } })
        } catch (e) {
            if (get()._countsToken !== token) return
            // Losing the aggregate must not cost the cards, which load
            // independently — the board still works, the headers just go stale.
        }
    },

    // Page 1 of every column, in parallel, plus the aggregate. The stage list
    // has to arrive first because its ids are what the column requests are keyed
    // by; everything after it is one round trip rather than one per page.
    //
    // `repFilter` is the CRM view's owner. Cards and counts cached for another
    // one are someone else's board, so they go now rather than when page 1
    // lands — the no-blink keep below is only for the same board — and the
    // tokens move so nothing still in flight for it can land.
    fetchPipeline: async (repFilter = get().repFilter) => {
        if (repFilter !== get().repFilter) {
            set((state) => ({
                repFilter,
                counts: emptyCounts(),
                byStage: Object.fromEntries(
                    state.stages.map((s) => [keyOf(s.id), { ...emptyColumn(), loading: true }])
                ),
                _tokens: Object.fromEntries(
                    Object.entries(state._tokens).map(([key, token]) => [key, token + 1])
                ),
                _countsToken: state._countsToken + 1,
            }))
        }
        set({ loading: true })
        let stages
        try {
            const stagesRes = await fetcher.getCrmStages()
            stages = (stagesRes?.data || [])
                .filter((s) => s.is_active)
                .sort((a, b) => a.order - b.order)
        } catch (e) {
            set({ loading: false })
            toast.error('Could not load the pipeline.')
            return
        }
        // A new column starts empty *and loading* so it never reads as "no
        // opportunities" before its first page answers. A loaded one keeps its
        // cards until page 1 replaces them, so returning to the board doesn't blink.
        set((state) => ({
            stages,
            byStage: Object.fromEntries(
                stages.map((s) => [
                    keyOf(s.id),
                    state.byStage[keyOf(s.id)] || { ...emptyColumn(), loading: true },
                ])
            ),
        }))
        await Promise.all([
            get().refreshCounts(),
            ...stages.map((s) => get().fetchStagePage(s.id, 1)),
        ])
        set({ loading: false })
    },

    // A new search term or rep filter is a new result set: every column starts
    // again from page 1 and the aggregate is re-read. Resetting the columns also
    // bumps their tokens, which is what stops an in-flight response for the
    // previous query from landing in the new one.
    applyFilters: async () => {
        const { stages } = get()
        set((state) => ({
            byStage: Object.fromEntries(
                stages.map((s) => [keyOf(s.id), { ...emptyColumn(), loading: true }])
            ),
            _tokens: Object.fromEntries(
                stages.map((s) => [keyOf(s.id), (state._tokens[keyOf(s.id)] || 0) + 1])
            ),
        }))
        await Promise.all([
            get().refreshCounts(),
            ...stages.map((s) => get().fetchStagePage(s.id, 1)),
        ])
    },

    // Targeted refresh after a mutation the board cannot resolve locally — a
    // pursuit arriving from, or leaving for, somewhere off the board. Reloads
    // page 1 of the named columns only, never the whole board, and re-reads the
    // aggregate. Cards past page 1 of those columns are dropped rather than
    // re-walked; the user scrolls back down if they want them.
    refreshStages: async (stageIds = []) => {
        // Falsy ids are dropped, not just null: a pursuit filed with no stage
        // (NewLeadModal leaves the select empty when the account has no active
        // ones) would otherwise send `stage=`, which the backend reads as "no
        // stage filter" and answers with the whole open pipeline.
        const ids = stageIds.filter(Boolean)
        await Promise.all([
            get().refreshCounts(),
            ...ids.map((id) => get().fetchStagePage(id, 1)),
        ])
    },

    opportunitiesForStage: (stageId) => get().byStage[keyOf(stageId)]?.rows || [],

    // Which column is holding a card, or null if it isn't loaded — it may sit on
    // a page nobody has scrolled to, or belong to a stage this board doesn't
    // show. Callers that must stay right either way follow up with the counts.
    _stageHolding: (opportunityId) =>
        Object.keys(get().byStage).find((key) =>
            get().byStage[key].rows.some((o) => o.id === opportunityId)
        ) || null,

    // Drop one card the caller has already had removed from the board's set —
    // deleted outright, or closed so it is no longer OPEN. The card goes
    // immediately so the board doesn't wait on the network, and the aggregate is
    // re-read because the headers can no longer be counted off the cards: the
    // pursuit may well have been on a page this board never loaded.
    removeOpportunity: (opportunityId) => get().removeOpportunities([opportunityId]),

    // The same, for a caller that has several to drop at once (deleting a person
    // takes all of their open pursuits). One pass and one counts request rather
    // than one of each per card.
    removeOpportunities: (opportunityIds) => {
        const ids = new Set(opportunityIds)
        if (ids.size === 0) return
        set((state) => ({
            byStage: Object.fromEntries(
                Object.entries(state.byStage).map(([key, column]) => [
                    key,
                    column.rows.some((o) => ids.has(o.id))
                        ? { ...column, rows: column.rows.filter((o) => !ids.has(o.id)) }
                        : column,
                ])
            ),
        }))
        get().refreshCounts()
    },

    // Replace one card the caller has already had a PATCH confirmed for — the
    // counterpart of removeOpportunity. An edit can move the card (a stage
    // change) or change who it counts for (a reassignment), so it is re-seated
    // into the right column and the aggregate re-read. A card this board isn't
    // holding is simply not matched.
    setOpportunity: (opportunityId, data) => {
        const from = get()._stageHolding(opportunityId)
        if (from === null) return
        const to = keyOf(data?.stage)
        set((state) => {
            const byStage = { ...state.byStage }
            if (from === to) {
                byStage[from] = {
                    ...byStage[from],
                    rows: byStage[from].rows.map((o) => (o.id === opportunityId ? data : o)),
                }
            } else {
                byStage[from] = {
                    ...byStage[from],
                    rows: byStage[from].rows.filter((o) => o.id !== opportunityId),
                }
                // Only into a column this board is showing; an edit that moves a
                // pursuit to a stage the board doesn't render just removes it.
                if (byStage[to]) {
                    byStage[to] = { ...byStage[to], rows: [data, ...byStage[to].rows] }
                }
            }
            return { byStage }
        })
        get().refreshCounts()
    },

    // Optimistically move a card to slot `index` of a column (its own or
    // another), counted without the card itself. 0 is the top.
    moveOpportunity: async (opportunityId, toStageId, index = 0) => {
        const from = get()._stageHolding(opportunityId)
        if (from === null) return
        const to = keyOf(toStageId)
        const target = get().byStage[from].rows.find((o) => o.id === opportunityId)
        if (!target) return

        const others = (get().byStage[to]?.rows || []).filter((o) => o.id !== opportunityId)
        const at = Math.max(0, Math.min(index, others.length))
        if (from === to && get().byStage[from].rows.indexOf(target) === at) return
        // The top is sent as no neighbours, so it is the column's real top
        // whatever the filters hide. Past the last loaded card, `below` is
        // left out and the server finds the true next one.
        const above = at > 0 ? others[at - 1].id : null
        const below = at > 0 && at < others.length ? others[at].id : null

        const prevByStage = get().byStage
        const prevCounts = get().counts
        // Both stages are known here, so the headers are adjusted arithmetically
        // rather than by asking the server — a drag stays instant, and the total
        // is untouched because the card hasn't left the board.
        const moved = { ...target, stage: toStageId }
        set((state) => {
            const byStage = { ...state.byStage }
            byStage[from] = {
                ...byStage[from],
                rows: byStage[from].rows.filter((o) => o.id !== opportunityId),
            }
            if (byStage[to]) {
                byStage[to] = { ...byStage[to], rows: [...others.slice(0, at), moved, ...others.slice(at)] }
            }
            if (from === to) return { byStage }
            return {
                byStage,
                counts: {
                    ...state.counts,
                    stages: {
                        ...state.counts.stages,
                        [from]: Math.max(0, (state.counts.stages[from] || 0) - 1),
                        [to]: (state.counts.stages[to] || 0) + 1,
                    },
                },
            }
        })

        try {
            const res = await fetcher.moveCrmOpportunity(opportunityId, { stage: toStageId, above, below })
            // Reconcile with the server's version (e.g. updated stage_detail).
            set((state) => ({
                byStage: state.byStage[to]
                    ? {
                        ...state.byStage,
                        [to]: {
                            ...state.byStage[to],
                            rows: state.byStage[to].rows.map((o) =>
                                o.id === opportunityId ? res.data : o
                            ),
                        },
                    }
                    : state.byStage,
            }))
        } catch (e) {
            set({ byStage: prevByStage, counts: prevCounts }) // roll back
            // A neighbour moved under us: the interceptor already toasted the
            // server's message, so just reload what this board got wrong.
            if (e?.response?.status === 409) {
                get().refreshStages(from === to ? [from] : [from, to])
                return
            }
            toast.error('Could not move that opportunity.')
        }
    },
}))
